import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Award, Gift, Heart, GraduationCap, Trophy, Bot, ScanFace, Brain,
  Star, Send, Check, ArrowRight, Sparkles, ArrowLeft, Menu, X
} from "lucide-react";

const TABS = [
  { id: "loyalty", label: "Carta Punti", icon: Award, color: "#EAB308" },
  { id: "cral", label: "CRAL", icon: Gift, color: "#F472B6" },
  { id: "achievements", label: "Badge", icon: Trophy, color: "#10B981" },
  { id: "training", label: "Formazione", icon: GraduationCap, color: "#8B5CF6" },
  { id: "wellbeing", label: "Wellbeing", icon: Heart, color: "#EC4899" },
  { id: "perfreview", label: "Mie Review", icon: Brain, color: "#6366F1" },
  { id: "face", label: "Volto", icon: ScanFace, color: "#06B6D4" },
  { id: "chatbot", label: "Assistente HR", icon: Bot, color: "#3B82F6" },
];

export default function EmployeeAdvancedPage() {
  const [tab, setTab] = useState("loyalty");
  const [mobileOpen, setMobileOpen] = useState(false);
  const T = TABS.find(t => t.id === tab);

  return (
    <div className="min-h-screen flex" style={{ background: "#0A0E27" }} data-testid="employee-advanced">
      {/* SIDEBAR (desktop) */}
      <aside className="hidden md:flex flex-col w-[260px] shrink-0 sticky top-0 h-screen border-r" style={{ background: "#0F1430", borderColor: "rgba(255,255,255,0.06)" }} data-testid="emp-adv-sidebar">
        <div className="p-5 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <Link to="/portal" data-testid="back-to-portal-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Torna al portale
          </Link>
        </div>
        <div className="px-5 pt-6 pb-3">
          <div className="text-[10.5px] font-bold tracking-[0.22em] uppercase" style={{ color: "rgba(255,255,255,0.40)" }}>Funzioni Premium</div>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {TABS.map(t => {
            const Icon = t.icon; const active = t.id === tab;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`emp-tab-${t.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all text-left"
                style={{
                  background: active ? `linear-gradient(135deg, ${t.color}25 0%, ${t.color}10 100%)` : "transparent",
                  color: active ? "#FFFFFF" : "rgba(255,255,255,0.65)",
                  border: active ? `1px solid ${t.color}50` : "1px solid transparent",
                }}>
                <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: active ? `linear-gradient(135deg, ${t.color} 0%, ${t.color}CC 100%)` : "rgba(255,255,255,0.05)" }}>
                  <Icon className="w-4 h-4" style={{ color: active ? "#FFFFFF" : t.color }} />
                </span>
                <span className="flex-1 truncate">{t.label}</span>
                {active && <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] font-bold mb-1" style={{ color: "rgba(255,255,255,0.35)" }}>Verixae · v16</div>
          <Link to="/portal" className="text-xs text-white/50 hover:text-white transition-colors" data-testid="back-to-portal-btn-bottom">← Portale dipendente</Link>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        {/* Mobile header */}
        <div className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 h-14 border-b backdrop-blur" style={{ background: "rgba(15,20,48,0.92)", borderColor: "rgba(255,255,255,0.08)" }}>
          <Link to="/portal" data-testid="mobile-back-portal" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80">
            <ArrowLeft className="w-4 h-4" /> Portale
          </Link>
          <div className="text-sm font-bold text-white">{T?.label}</div>
          <button onClick={() => setMobileOpen(o => !o)} data-testid="mobile-menu-toggle"
            className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)", color: "#FFFFFF" }}>
            {mobileOpen ? <X className="w-4 h-4"/> : <Menu className="w-4 h-4"/>}
          </button>
        </div>

        {/* Mobile slide-out menu */}
        {mobileOpen && (
          <div className="md:hidden border-b" style={{ background: "#0F1430", borderColor: "rgba(255,255,255,0.06)" }} data-testid="mobile-menu">
            <div className="p-3 space-y-1">
              {TABS.map(t => {
                const Icon = t.icon; const active = t.id === tab;
                return (
                  <button key={t.id} onClick={() => { setTab(t.id); setMobileOpen(false); }}
                    data-testid={`mobile-tab-${t.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-left"
                    style={{
                      background: active ? `${t.color}20` : "transparent",
                      color: active ? "#FFFFFF" : "rgba(255,255,255,0.70)",
                    }}>
                    <Icon className="w-4 h-4" style={{ color: t.color }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="p-5 md:p-8 max-w-[1100px] mx-auto">
          <div className="mb-6 hidden md:block">
            <div className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: T?.color }}>— {T?.label}</div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">I miei strumenti</h1>
            <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>Welfare, premi, formazione, assistente HR e altro.</p>
          </div>
          <div data-testid={`emp-panel-${tab}`}>
            {tab === "loyalty" && <LoyaltyCard/>}
            {tab === "cral" && <CralCatalog/>}
            {tab === "achievements" && <MyAchievements/>}
            {tab === "training" && <MyTraining/>}
            {tab === "wellbeing" && <WellbeingSurvey/>}
            {tab === "perfreview" && <MyPerfReviews/>}
            {tab === "face" && <FaceEnroll/>}
            {tab === "chatbot" && <Chatbot/>}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ---------------- LOYALTY CARD ---------------- */
function LoyaltyCard() {
  const [data, setData] = useState({ balance: 0, tier: "BRONZE", history: [] });
  useEffect(() => { (async () => { try { setData((await api.get("/loyalty/me")).data); } catch (_) {} })(); }, []);
  const tierColor = { BRONZE: "#F59E0B", SILVER: "#E2E8F0", GOLD: "#FBBF24", PLATINUM: "#E9D5FF" }[data.tier];
  const tierBg = {
    BRONZE: "linear-gradient(135deg, #0F172A 0%, #7C2D12 60%, #B45309 100%)",
    SILVER: "linear-gradient(135deg, #0F172A 0%, #334155 60%, #94A3B8 100%)",
    GOLD: "linear-gradient(135deg, #1F1B0F 0%, #78350F 50%, #B45309 100%)",
    PLATINUM: "linear-gradient(135deg, #1E1B4B 0%, #4C1D95 50%, #7C3AED 100%)",
  }[data.tier];
  return (
    <div className="space-y-4">
      <div className="rounded-3xl p-6 text-white relative overflow-hidden" style={{ background: tierBg }} data-testid="loyalty-card">
        <div className="text-[10px] font-mono uppercase tracking-[0.20em] font-bold mb-2 opacity-80">Verixae Loyalty Card</div>
        <div className="text-3xl font-extrabold mt-3" style={{ color: tierColor }}>{data.balance} <span className="text-base font-bold opacity-80">punti</span></div>
        <div className="mt-4 inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider" style={{ background: tierColor + "30", color: tierColor }}>{data.tier}</div>
        <div className="text-xs opacity-60 mt-4">{data.tier === "BRONZE" ? `${250 - data.balance} punti per SILVER` : data.tier === "SILVER" ? `${500 - data.balance} per GOLD` : data.tier === "GOLD" ? `${750 - data.balance} per PLATINUM` : "Massimo livello!"}</div>
      </div>
      <div className="rounded-2xl p-5 border" style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)" }}>
        <h3 className="font-bold mb-3 text-white">Storico transazioni</h3>
        <div className="space-y-1 text-sm" data-testid="loyalty-history">
          {data.history.map(t => (
            <div key={t.id} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
              <div>
                <div className="font-medium text-white/90">{t.reason}</div>
                <div className="text-xs text-white/50">{new Date(t.created_at).toLocaleDateString("it-IT")}</div>
              </div>
              <div className={`font-mono font-bold ${t.delta>0?'text-emerald-400':'text-rose-400'}`}>{t.delta>0?'+':''}{t.delta} pt</div>
            </div>
          ))}
          {data.history.length === 0 && <div className="text-center text-white/40 py-4">Nessuna transazione</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- CRAL CATALOG ---------------- */
function CralCatalog() {
  const [items, setItems] = useState([]);
  const [redeemed, setRedeemed] = useState([]);
  const load = async () => {
    try {
      setItems((await api.get("/cral/partners")).data.items || []);
      setRedeemed((await api.get("/cral/my-redemptions")).data.items || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  const redeem = async (pid) => {
    try {
      const r = await api.post(`/cral/redeem/${pid}`);
      toast.success(`Codice: ${r.data.code}`); load();
    } catch (e) { toast.error("Errore"); }
  };
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3" data-testid="cral-partners">
        {items.map(p => (
          <div key={p.id} className="rounded-2xl p-5 bg-white border border-pink-200">
            <div className="text-[10px] font-bold uppercase tracking-wide text-pink-700 mb-1">{p.category}</div>
            <div className="text-xl font-bold">{p.name}</div>
            <div className="text-3xl font-extrabold text-emerald-600 mt-1">-{p.discount_pct}%</div>
            {p.description && <div className="text-xs text-slate-500 mt-2">{p.description}</div>}
            <Button onClick={()=>redeem(p.id)} data-testid={`cral-redeem-${p.id}`} className="mt-3 w-full rounded-xl bg-pink-500 hover:bg-pink-600">Ottieni codice</Button>
          </div>
        ))}
      </div>
      <div className="rounded-2xl p-5 bg-white border border-slate-200">
        <h3 className="font-bold mb-3">I miei codici sconto</h3>
        <div className="space-y-2 text-sm" data-testid="cral-my-codes">
          {redeemed.map(r => (
            <div key={r.id} className="p-3 rounded-xl bg-slate-50 flex items-center justify-between">
              <div>
                <div className="font-semibold">{r.partner_name} <span className="text-emerald-600 font-bold">-{r.discount_pct}%</span></div>
                <div className="font-mono text-xs mt-1 text-slate-700">{r.code}</div>
              </div>
              <div className="text-xs text-slate-500">scade {r.expires_at?.slice(0,10)}</div>
            </div>
          ))}
          {redeemed.length === 0 && <div className="text-center text-slate-500 py-3">Nessun codice ancora riscattato</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- ACHIEVEMENTS ---------------- */
function MyAchievements() {
  const [data, setData] = useState({ items: [], unlocked_count: 0, total: 0 });
  useEffect(() => { (async () => { try { setData((await api.get("/achievements/me")).data); } catch (_) {} })(); }, []);
  return (
    <div>
      <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-500 to-teal-600 text-white mb-4">
        <div className="text-sm opacity-80">Badge sbloccati</div>
        <div className="text-4xl font-extrabold">{data.unlocked_count} <span className="text-base font-bold opacity-80">/ {data.total}</span></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="achievements-grid">
        {data.items.map(a => (
          <div key={a.code} className={`rounded-2xl p-4 border-2 transition-all ${a.unlocked?'bg-white':'bg-slate-50 opacity-50'}`} style={{ borderColor: a.unlocked ? a.color : "#E2E8F0" }}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ background: a.unlocked ? `linear-gradient(135deg, ${a.color} 0%, ${a.color}CC 100%)` : "#94A3B8" }}>
                {a.unlocked ? <Check className="w-6 h-6"/> : <Trophy className="w-5 h-5"/>}
              </div>
              <div className="flex-1">
                <div className="font-bold">{a.title}</div>
                <div className="text-xs text-slate-600">{a.desc}</div>
                <div className="text-xs font-bold mt-1" style={{ color: a.color }}>+{a.points} pt</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- MY TRAINING ---------------- */
function MyTraining() {
  const [items, setItems] = useState([]);
  const load = async () => { try { setItems((await api.get("/training/mine")).data.items || []); } catch (_) {} };
  useEffect(() => { load(); }, []);
  const complete = async (id) => {
    try { await api.post(`/training/enrollments/${id}/complete`); toast.success("Corso completato 🎉"); load(); } catch (e) { toast.error("Errore"); }
  };
  return (
    <div className="space-y-3" data-testid="my-training">
      {items.map(t => (
        <div key={t.id} className="rounded-2xl p-5 bg-white border border-slate-200">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-violet-700">{t.course_category}</div>
              <div className="font-bold text-lg">{t.course_title}</div>
              <div className="text-xs text-slate-500">Iscritto il {t.enrolled_at?.slice(0,10)}</div>
              {t.expires_at && <div className="text-xs text-amber-600 mt-1">⏰ Scade il {t.expires_at?.slice(0,10)}</div>}
            </div>
            {t.status === "pending" ? (
              <Button onClick={()=>complete(t.id)} className="rounded-xl bg-violet-500 hover:bg-violet-600">Segna completato</Button>
            ) : (
              <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">✓ Completato</span>
            )}
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="text-center text-slate-500 py-8">Nessun corso assegnato</div>}
    </div>
  );
}

/* ---------------- WELLBEING SURVEY ---------------- */
function WellbeingSurvey() {
  const [canRespond, setCanRespond] = useState(null);
  const [mood, setMood] = useState(3);
  const [stress, setStress] = useState(3);
  const [workload, setWorkload] = useState(3);
  const [teamFeel, setTeamFeel] = useState(3);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => { (async () => { try { setCanRespond((await api.get("/wellbeing/can-respond")).data); } catch (_) {} })(); }, []);
  const submit = async () => {
    try {
      await api.post("/wellbeing/respond", { mood, stress, workload, team_feel: teamFeel, comment: comment || null });
      setSubmitted(true); toast.success("Grazie per il feedback (anonimo) 💜");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const Scale = ({ label, value, setValue, leftEmoji, rightEmoji }) => (
    <div className="py-3">
      <div className="flex justify-between text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
        <span>{leftEmoji} {label}</span><span>{value}/5 {rightEmoji}</span>
      </div>
      <div className="flex gap-2">
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={()=>setValue(n)} data-testid={`${label}-${n}`}
            className="flex-1 h-12 rounded-xl font-bold text-sm transition-all"
            style={{
              background: n <= value ? "linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)" : "#F1F5F9",
              color: n <= value ? "#FFFFFF" : "#94A3B8",
            }}>{n}</button>
        ))}
      </div>
    </div>
  );
  if (canRespond && !canRespond.can_respond && !submitted) {
    return <div className="rounded-2xl p-8 text-center bg-emerald-50 border border-emerald-200" data-testid="wellbeing-already">
      <Heart className="w-12 h-12 mx-auto text-emerald-500 mb-3"/>
      <div className="font-bold">Grazie!</div>
      <div className="text-sm text-slate-600 mt-1">Hai già risposto al pulse di questa settimana ({canRespond.week}). A presto!</div>
    </div>;
  }
  if (submitted) {
    return <div className="rounded-2xl p-8 text-center bg-pink-50 border border-pink-200" data-testid="wellbeing-success">
      <Sparkles className="w-12 h-12 mx-auto text-pink-500 mb-3"/>
      <div className="font-bold text-lg">Grazie per la tua voce 💜</div>
      <div className="text-sm text-slate-600 mt-1">La tua risposta è completamente anonima. Hai contribuito al benessere del team.</div>
    </div>;
  }
  return (
    <div className="rounded-2xl p-6 bg-white border border-pink-200" data-testid="wellbeing-form">
      <div className="text-center mb-5">
        <Heart className="w-10 h-10 mx-auto text-pink-500 mb-2"/>
        <h3 className="text-lg font-bold">Pulse settimanale (anonimo)</h3>
        <p className="text-xs text-slate-500 mt-1">La tua risposta NON è collegata al tuo profilo. È completamente anonima.</p>
      </div>
      <Scale label="Umore" value={mood} setValue={setMood} leftEmoji="😢" rightEmoji="😄"/>
      <Scale label="Stress" value={stress} setValue={setStress} leftEmoji="😌" rightEmoji="😰"/>
      <Scale label="Carico" value={workload} setValue={setWorkload} leftEmoji="🌱" rightEmoji="🔥"/>
      <Scale label="Team" value={teamFeel} setValue={setTeamFeel} leftEmoji="🚶" rightEmoji="🤝"/>
      <textarea value={comment} onChange={e=>setComment(e.target.value)} placeholder="Vuoi aggiungere un commento (anonimo)?" data-testid="wellbeing-comment" rows={3} maxLength={500} className="w-full mt-3 px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none"/>
      <Button onClick={submit} data-testid="wellbeing-submit" className="mt-4 w-full h-12 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white">Invia anonimamente</Button>
    </div>
  );
}

/* ---------------- MY PERF REVIEWS ---------------- */
function MyPerfReviews() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [answers, setAnswers] = useState([]);
  const load = async () => { try { setItems((await api.get("/perf-review/mine")).data.items || []); } catch (_) {} };
  useEffect(() => { load(); }, []);
  const submit = async (rid) => {
    if (answers.some(a => !a?.trim())) return toast.error("Rispondi a tutte le domande");
    try { await api.post(`/perf-review/${rid}/answer`, { answers }); toast.success("Risposte inviate"); load(); setOpenId(null); } catch (e) { toast.error("Errore"); }
  };
  return (
    <div className="space-y-3" data-testid="my-reviews">
      {items.map(r => (
        <div key={r.id} className="rounded-2xl p-5 bg-white border border-slate-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-bold">{r.period}</div>
              <div className="text-xs text-slate-500">{r.status === "pending" ? "Da compilare" : r.status === "answered" ? "In attesa di sintesi AI" : "Completata"}</div>
            </div>
            <Button onClick={()=>{ setOpenId(openId===r.id?null:r.id); setAnswers(r.answers?.length?r.answers:new Array(r.questions?.length||0).fill("")); }} variant="outline" className="rounded-xl">{r.status==="pending"?"Rispondi":"Vedi"}</Button>
          </div>
          {openId === r.id && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              {r.questions?.map((q, i) => (
                <div key={i}>
                  <div className="font-semibold text-sm">Q{i+1}: {q}</div>
                  {r.status === "pending" ? (
                    <textarea value={answers[i] || ""} onChange={e=>{const n=[...answers];n[i]=e.target.value;setAnswers(n);}} rows={3} className="w-full mt-2 px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none" data-testid={`pr-answer-${i}`}/>
                  ) : (
                    <div className="mt-1 text-slate-700 pl-3 border-l-2 border-indigo-300 text-sm">{r.answers?.[i]}</div>
                  )}
                </div>
              ))}
              {r.ai_summary && <div className="mt-3 p-3 rounded-xl bg-indigo-50 border border-indigo-200 whitespace-pre-wrap text-sm">{r.ai_summary}</div>}
              {r.status === "pending" && <Button onClick={()=>submit(r.id)} className="rounded-xl bg-indigo-500 hover:bg-indigo-600 mt-2" data-testid={`pr-submit-${r.id}`}>Invia risposte</Button>}
            </div>
          )}
        </div>
      ))}
      {items.length === 0 && <div className="text-center text-slate-500 py-8">Nessuna review assegnata</div>}
    </div>
  );
}

/* ---------------- FACE ENROLL ---------------- */
function FaceEnroll() {
  const [status, setStatus] = useState({ enrolled: false });
  const [photo, setPhoto] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const videoRef = useRef(null); const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const load = async () => { try { setStatus((await api.get("/face/status")).data); } catch (_) {} };
  useEffect(() => { load(); return () => stopCamera(); }, []);
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      if (videoRef.current) { videoRef.current.srcObject = stream; setStreaming(true); }
    } catch (e) { toast.error("Impossibile accedere alla fotocamera"); }
  };
  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      setStreaming(false);
    }
  };
  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current; const c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/jpeg", 0.7);
    setPhoto(dataUrl); stopCamera();
  };
  const enroll = async () => {
    try { await api.post("/face/enroll", { photo_base64: photo }); toast.success("Riferimento registrato"); load(); setPhoto(""); } catch (e) { toast.error("Errore"); }
  };
  const verify = async () => {
    setVerifying(true); setVerifyResult(null);
    try { const r = await api.post("/face/verify", { photo_base64: photo }); setVerifyResult(r.data); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setVerifying(false); }
  };
  return (
    <div className="rounded-2xl p-6 bg-white border border-slate-200" data-testid="face-panel">
      <h3 className="text-lg font-bold mb-2 flex items-center gap-2"><ScanFace className="w-5 h-5 text-cyan-500"/> Riconoscimento facciale</h3>
      <p className="text-xs text-slate-500 mb-4">Registra una foto di riferimento per timbrature biometriche opzionali. Conforme GDPR.</p>
      <div className="text-sm mb-4">Stato: {status.enrolled ? <span className="text-emerald-600 font-bold">✓ Registrato {status.enrolled_at?.slice(0,10)}</span> : <span className="text-slate-500">Non registrato</span>}</div>

      {!streaming && !photo && (
        <Button onClick={startCamera} className="rounded-xl bg-cyan-500 hover:bg-cyan-600" data-testid="face-start-camera"><ScanFace className="w-4 h-4 mr-1"/> Apri fotocamera</Button>
      )}
      {streaming && (
        <div className="space-y-3">
          <video ref={videoRef} autoPlay playsInline className="w-full max-w-[400px] rounded-2xl bg-slate-900"/>
          <div className="flex gap-2">
            <Button onClick={capture} className="rounded-xl bg-cyan-500" data-testid="face-capture">Cattura</Button>
            <Button onClick={stopCamera} variant="outline" className="rounded-xl">Annulla</Button>
          </div>
        </div>
      )}
      {photo && (
        <div className="space-y-3">
          <img src={photo} alt="captured" className="w-full max-w-[400px] rounded-2xl"/>
          <div className="flex gap-2 flex-wrap">
            {!status.enrolled && <Button onClick={enroll} className="rounded-xl bg-emerald-500" data-testid="face-enroll-btn">Salva come riferimento</Button>}
            {status.enrolled && <Button onClick={verify} disabled={verifying} className="rounded-xl bg-blue-500" data-testid="face-verify-btn">{verifying ? "Verifica AI in corso..." : "Verifica match AI"}</Button>}
            <Button onClick={()=>{setPhoto(""); setVerifyResult(null);}} variant="outline" className="rounded-xl">Riprova</Button>
          </div>
          {verifyResult && (
            <div className={`p-4 rounded-xl ${verifyResult.match?'bg-emerald-50 border border-emerald-300':'bg-rose-50 border border-rose-300'}`} data-testid="face-result">
              <div className="font-bold">{verifyResult.match ? "✓ MATCH" : "✗ NO MATCH"}</div>
              <div className="text-sm">Confidenza: <strong>{verifyResult.confidence}%</strong></div>
              {verifyResult.reason && <div className="text-xs text-slate-600 mt-1">{verifyResult.reason}</div>}
            </div>
          )}
        </div>
      )}
      <canvas ref={canvasRef} className="hidden"/>
    </div>
  );
}

/* ---------------- CHATBOT ---------------- */
function Chatbot() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [conv, setConv] = useState(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setMessages(m => [...m, { role: "user", text: msg }]);
    setInput(""); setSending(true);
    try {
      const r = await api.post("/chatbot/ask", { message: msg, conversation_id: conv });
      setConv(r.data.conversation_id);
      setMessages(m => [...m, { role: "assistant", text: r.data.reply, ticket_id: r.data.ticket_id }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", text: "Errore. Riprova." }]);
    } finally { setSending(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 flex flex-col" style={{ height: 560 }} data-testid="chatbot-panel">
      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white"><Bot className="w-4 h-4"/></div>
        <div>
          <div className="font-bold">Assistente HR</div>
          <div className="text-xs text-emerald-600">● Online · GPT-5.2</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
        {messages.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-12">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-blue-400"/>
            Ciao! Chiedimi qualcosa: "Quante ferie ho?" · "Quando arriva il prossimo cedolino?" · "Vorrei richiedere un permesso"
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role==='user'?'justify-end':'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role==='user'?'bg-blue-500 text-white':'bg-slate-100 text-slate-800'}`}>
              {m.text}
              {m.ticket_id && <div className="mt-2 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 inline-block">🎫 Ticket aperto: {m.ticket_id.slice(0,8)}</div>}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="bg-slate-100 text-slate-500 text-xs px-4 py-2 rounded-2xl">L'assistente sta scrivendo...</div></div>}
        <div ref={endRef}/>
      </div>
      <div className="p-3 border-t border-slate-100 flex gap-2">
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter')send(); }} placeholder="Scrivi un messaggio..." data-testid="chatbot-input" className="flex-1 px-4 h-11 rounded-full bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-blue-400"/>
        <Button onClick={send} disabled={sending || !input.trim()} data-testid="chatbot-send" className="rounded-full bg-blue-500 hover:bg-blue-600 w-11 h-11 p-0"><Send className="w-4 h-4"/></Button>
      </div>
    </div>
  );
}
